import type { RenderContext, VideoFrame } from '../../types';
import { WebGLShaderProgram } from '../../gpu/shader';
import { BaseEffect } from '../base';
import { VERTEX_FULLSCREEN, FRAG_ANAMORPHIC_STREAK } from '../../shaders/glsl';
import { withOverlay } from '../environmental/composite';

const DEFAULTS = { intensity: 0.7, width: 0.45, positionY: 0.35, hue: 0.12 };

let anaProg: WebGLShaderProgram | null = null;

export class AnamorphicStreakEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'anamorphic_streak', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!anaProg) anaProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_ANAMORPHIC_STREAK);
    return this.runShaderPass(frame, anaProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_intensity', params.intensity);
      prog.setUniform1f('u_width', params.width);
      prog.setUniform1f('u_positionY', params.positionY);
      prog.setUniform1f('u_hue', params.hue);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, _context: RenderContext): VideoFrame {
    return withOverlay(frame, (ctx, w, h) => {
      const cy = params.positionY * h;
      const band = Math.max(4, params.width * h * 0.08);
      const g = ctx.createLinearGradient(0, cy - band * 3, 0, cy + band * 3);
      g.addColorStop(0, 'rgba(255,200,120,0)');
      g.addColorStop(0.45, `rgba(255,210,160,${0.15 * params.intensity})`);
      g.addColorStop(0.5, `rgba(255,240,220,${0.55 * params.intensity})`);
      g.addColorStop(0.55, `rgba(180,210,255,${0.2 * params.intensity})`);
      g.addColorStop(1, 'rgba(120,180,255,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      const streak = ctx.createLinearGradient(0, cy, w, cy);
      streak.addColorStop(0, 'rgba(255,220,180,0)');
      streak.addColorStop(0.2, `rgba(255,230,200,${0.35 * params.intensity})`);
      streak.addColorStop(0.5, `rgba(255,245,230,${0.7 * params.intensity})`);
      streak.addColorStop(0.8, `rgba(200,220,255,${0.35 * params.intensity})`);
      streak.addColorStop(1, 'rgba(160,200,255,0)');
      ctx.fillStyle = streak;
      ctx.fillRect(0, cy - band * 0.35, w, band * 0.7);
    }, 'lighter');
  }
}
